import React, { useState, useEffect } from 'react';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';

export interface ChangeEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentEmail: string;
    onSubmit: (newEmail: string) => Promise<void>;
    isLoading?: boolean;
    error?: string;
}

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({
    isOpen,
    onClose,
    currentEmail,
    onSubmit,
    isLoading = false,
    error,
}) => {
    const [newEmail, setNewEmail] = useState('');
    const [validationError, setValidationError] = useState('');

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setNewEmail('');
            setValidationError('');
        }
    }, [isOpen]);

    const validateEmail = (email: string): string => {
        if (!email.trim()) {
            return 'Email is required';
        }

        // Basic email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address';
        }

        if (email.length > 254) {
            return 'Email address is too long';
        }

        if (email.toLowerCase() === currentEmail.toLowerCase()) {
            return 'New email must be different from current email';
        }

        return '';
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewEmail(value);

        // Clear validation error when user starts typing
        if (validationError) {
            setValidationError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validation = validateEmail(newEmail);
        if (validation) {
            setValidationError(validation);
            return;
        }

        try {
            await onSubmit(newEmail);
        } catch (err) {
            // Error handling is managed by parent component
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    const contentStyles: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    };

    const currentEmailStyles: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    };

    const labelStyles: React.CSSProperties = {
        fontSize: '13px',
        fontWeight: 500,
        color: 'var(--text-low_em)',
        margin: 0,
    };

    const currentValueStyles: React.CSSProperties = {
        fontSize: '15px',
        fontWeight: 500,
        color: 'var(--text-high_em)',
        padding: '12px',
        backgroundColor: 'var(--surface-surface_2)',
        borderRadius: '12px',
        border: '1px solid var(--rare-border)',
        margin: 0,
    };

    const buttonGroupStyles: React.CSSProperties = {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '8px',
    };

    const displayError = error || validationError;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Change Email"
            size="md"
            closeOnOverlayClick={!isLoading}
            closeOnEscape={!isLoading}
        >
            <form onSubmit={handleSubmit} style={contentStyles}>
                <div style={currentEmailStyles}>
                    <label style={labelStyles}>Current Email</label>
                    <div style={currentValueStyles}>{currentEmail}</div>
                </div>

                <Input
                    label="New Email"
                    type="email"
                    value={newEmail}
                    onChange={handleEmailChange}
                    placeholder="Enter new email address"
                    error={displayError}
                    disabled={isLoading}
                    required
                    autoFocus
                />

                <div style={buttonGroupStyles}>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        isLoading={isLoading}
                        disabled={!newEmail.trim() || isLoading}
                    >
                        Change Email
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default ChangeEmailModal;
